/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2025-12-10
 🧩 Description:
    API functions for Log Viewer module
 ────────────────────────────────────────────────────────────────*/

import { api } from '../../lib/api';

const API_BASE = '/api/telephony/log-viewer';

export type LogType = 'freeswitch' | 'fail2ban';

export interface LogEntry {
    lineNumber: number;
    timestamp?: string;
    level?: string;
    content: string;
}

export interface LogViewerParams {
    type: LogType;
    lines?: number;
    search?: string;
    level?: string;
    tail?: boolean;
    offset?: number;
}

export interface LogViewerResponse {
    success: boolean;
    data: {
        logType: LogType;
        fileName: string;
        entries: LogEntry[];
        totalLines: number;
        hasMore: boolean;
    };
}

export interface LogFileStatus {
    available: boolean;
    path: string;
    size?: number;
    lastModified?: string;
}

export interface LogFilesStatusResponse {
    success: boolean;
    data: {
        freeswitch: LogFileStatus;
        fail2ban: LogFileStatus;
    };
}

// Get log files status
export async function getLogFilesStatus(): Promise<LogFilesStatusResponse> {
    const response = await api.get<LogFilesStatusResponse>(`${API_BASE}/status`);
    return response.data;
}

// Get log entries
export async function getLogEntries(params: LogViewerParams): Promise<LogViewerResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('type', params.type);
    if (params.lines) queryParams.append('lines', params.lines.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.level) queryParams.append('level', params.level);
    if (params.tail !== undefined) queryParams.append('tail', params.tail.toString());
    if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());

    const response = await api.get<LogViewerResponse>(`${API_BASE}?${queryParams.toString()}`);
    return response.data;
}

// Clear log file
export async function clearLog(type: LogType): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(`${API_BASE}/${type}/clear`);
    return response.data;
}

// Download log file URL
export function getLogDownloadUrl(type: LogType): string {
    return `${API_BASE}/${type}/download`;
}
